import { createBrowserRouter } from 'react-router';
import { App } from './App';
import { Landing } from './pages/Landing';
import { AuthCallback } from './pages/AuthCallback';
import { AuthGuard } from './components/AuthGuard';

/**
 * Routes per CLAUDE.md §6.1.
 * /preview/:id deferred until preview is wired.
 *
 * /tear-test is a dev-only playground for tuning the tear engine.
 * Lazy-loaded via router's own lazy so PixiJS only loads when the
 * playground (or future /reveal/:id) is visited — keeps the main bundle small.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    Component: App,
    children: [
      { index: true, Component: Landing },
      {
        // Lazy — Create pulls in the cards module + framer-motion (loader).
        // Keep them off the landing/reveal bundle (CLAUDE.md §14 budget).
        path: 'create',
        lazy: async () => {
          const { Create } = await import('./pages/Create');
          return {
            Component: () => (
              <AuthGuard>
                <Create />
              </AuthGuard>
            ),
          };
        },
      },
      {
        // Lazy — Dashboard pulls in framer-motion (card animations + confirm
        // dialog). Keep it off the landing/reveal bundle (CLAUDE.md §14 budget).
        path: 'dashboard',
        lazy: async () => {
          const { Dashboard } = await import('./pages/Dashboard');
          return {
            Component: () => (
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            ),
          };
        },
      },
      {
        // Lazy — pulls in PixiJS + framer-motion; keep them off the landing
        // bundle (CLAUDE.md §8 Directive D).
        path: 'reveal/:voucherId',
        lazy: async () => {
          const { Reveal } = await import('./pages/Reveal');
          return { Component: Reveal };
        },
      },
      { path: 'auth/callback', Component: AuthCallback },
      {
        // Static explainer; the embedded tear demo loads PixiJS only on tap,
        // so this route stays light (CLAUDE.md §14 budget).
        path: 'how-it-works',
        lazy: async () => {
          const { HowItWorks } = await import('./pages/HowItWorks');
          return { Component: HowItWorks };
        },
      },
      // Public pricing + legal pages (required by Paddle). Text-only, so
      // lazy-loaded to stay off the landing/reveal bundle.
      {
        path: 'pricing',
        lazy: async () => {
          const { Pricing } = await import('./pages/Pricing');
          return { Component: Pricing };
        },
      },
      {
        path: 'terms',
        lazy: async () => {
          const { Terms } = await import('./pages/Terms');
          return { Component: Terms };
        },
      },
      {
        path: 'privacy',
        lazy: async () => {
          const { Privacy } = await import('./pages/Privacy');
          return { Component: Privacy };
        },
      },
      {
        path: 'refunds',
        lazy: async () => {
          const { Refunds } = await import('./pages/Refunds');
          return { Component: Refunds };
        },
      },
      {
        // Paddle default payment link target (handles ?_ptxn= link flows).
        path: 'checkout',
        lazy: async () => {
          const { Checkout } = await import('./pages/Checkout');
          return { Component: Checkout };
        },
      },
      {
        path: 'tear-test',
        lazy: async () => {
          const { TearTest } = await import('./pages/TearTest');
          return { Component: TearTest };
        },
      },
      {
        // Dev-only playground for previewing card designs with live
        // controls. Mirrors /tear-test in shape and intent.
        path: 'card-test',
        lazy: async () => {
          const { CardTest } = await import('./pages/CardTest');
          return { Component: CardTest };
        },
      },
      {
        // Dev-only playground for tuning the envelope ceremony (§6.7).
        path: 'envelope-test',
        lazy: async () => {
          const { EnvelopeTest } = await import('./pages/EnvelopeTest');
          return { Component: EnvelopeTest };
        },
      },
    ],
  },
]);
